// @flow
import React from "react";
import shallowCompare from "react-addons-shallow-compare";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Sticky from "react-stickynode";
import Waypoint from "react-waypoint";
import BookmarkButtonAlt from "backpack-ui/dist/components/bookmarkButtonAlt";
import throttle from "lodash/throttle";
import memoize from "lodash/memoize";
import find from "lodash/find";
import Breadcrumbs from "./breadcrumbs";
import ContentBlock from "./contentBlock";
import PageHeader from "./pageHeader";
import EssentialInfo from "./essentialInfo";
import ContentHeader from "./contentHeader";
import { connected as NearbyPlaces } from "./nearbyPlaces";
import Callout from "./callout";
import EditLink from "./editLink";
import editUrl from "../utils/editUrl";
import ImagesContainer from "./imagesContainer";
import ShareMenu from "./shareMenu";
import ContactBar from "./contactBar";
import FeatureDiscover from "./featureDiscover";
import DiscoveryFlyout from "./featureDiscover/discoveryFlyout";
import Amenities from "./amenities";
import { connected as Booking } from "./booking";
import SidebarSection from "./sidebarSection";
import styles from "./styles";
import { createListUrl, createDetailUrlFromResource } from "../../../../server/utils/createUrls";
import { poiTypes } from "../../../../server/lib/open-planet/poiTypes";
import { bookingDotcomUrl, hostelWorldUrl } from "../utils/bookingUrl";
import analytics from "../../shared/actions/analytics";
import cleanUp from "../utils/cleanUp";
import actions from "../actions";
import getAvailability from "../selectors/getAvailability";
import hotelProvider from "../../shared/hotelProvider";
import selectDates from "../../shared/actions/selectDates";
import DataLayer from "../../shared/components/dataLayer";
import type {
  BookingInfo,
  Provider,
  Availability,
  HotelPoiComponentProps,
  WaypointLeaveEvent,
} from "../../../../server/types";

const _ = { throttle, find, memoize };

function addItemToAmenitiesGroup(title, item, groups) {
  const group = _.find(groups, (i) => i.title === title);

  if (group) {
    group.items.push(item);
  } else {
    groups.push({
      title,
      items: [
        item,
      ],
      capitalize: true,
    });
  }
  return groups;
}

/**
 * Renders a bookable hotel POI
 */
export default class HotelPoiComponent extends React.Component {
  static _getAmenities(facilities) {
    return facilities.reduce((memo, accItem) => {
      switch (accItem) {
        case "pets allowed":
          memo.push({
            title: "Pets",
            items: [
              "Pets are allowed. Charges may apply.",
            ],
          });
          break;
        case "fitness centre":
        case "indoor pool (all year)":
        case "indoor pool":
        case "sauna":
        case "spa and wellness centre":
        case "massage":
        case "hot tub/jacuzzi":
        case "outdoor pool (seasonal)":
        case "outdoor pool":
        case "golf course (within 3 km)":
          addItemToAmenitiesGroup("Activities", accItem, memo);
          break;
        case "free wifi":
          memo.push({
            title: "Internet",
            items: [
              "Free WiFi available",
            ],
          });
          break;
        case "private parking":
        case "valet parking":
          addItemToAmenitiesGroup("Parking", accItem, memo);
          break;
        case "restaurant":
        case "bar":
        case "restaurant (buffet)":
        case "restaurant (à la carte)":
          addItemToAmenitiesGroup("Food and Drink", accItem, memo);
          break;
        case "room service":
        case "dry cleaning":
        case "business centre":
        case "luggage storage":
        case "24-hour front desk":
        case "fax/photocopying":
        case "express check-in/check-out":
        case "meeting/banquet facilities":
        case "vip room facilities":
        case "shoeshine":
        case "airport shuttle (surcharge)":
        case "airport shuttle (free)":
        case "shuttle service (surcharge)":
        case "concierge service":
        case "car hire":
        case "ironing service":
        case "laundry":
        case "daily maid service":
          addItemToAmenitiesGroup("Services", accItem, memo);
          break;
        case "newspapers":
        case "vending machine (drinks)":
        case "safety deposit box":
        case "atm/cash machine on site":
        case "non-smoking rooms":
        case "facilities for disabled guests":
        case "non-smoking throughout":
        case "air conditioning":
        case "heating":
        case "lift":
          addItemToAmenitiesGroup("General", accItem, memo);
          break;
        default:
          addItemToAmenitiesGroup("Other", accItem, memo);
          break;
      }
      return memo;
    }, []);
  }

  constructor() {
    super();

    this.state = {
      nearby: false,
      hideMap: false,
    };

    this.getPoiUrl = this.getPoiUrl.bind(this);
    this.enterNearbyPlaces = this.enterNearbyPlaces.bind(this);
    this.leaveNearbyPlaces = this.leaveNearbyPlaces.bind(this);
    this._checkAvailability = this._checkAvailability.bind(this);
    this._book = this._book.bind(this);
  }

  state: {
    hideMap: boolean,
    hideMap: boolean,
  };

  shouldComponentUpdate(nextProps: Object, nextState: Object) {
    return shallowCompare(this, nextProps, nextState);
  }

  getPoiUrl: () => string;

  getPoiUrl() {
    const { poi, params, breadcrumb } = this.props;

    return createDetailUrlFromResource(poi, breadcrumb, params.placeId);
  }

  props: HotelPoiComponentProps;
  enterNearbyPlaces: () => void;
  leaveNearbyPlaces: () => void;
  _checkAvailability: () => void;
  _book: () => void;

  enterNearbyPlaces() {
    this.setState({
      hideMap: true,
    });
  }

  leaveNearbyPlaces({ currentPosition }: WaypointLeaveEvent) {
    this.setState({
      hideMap: (currentPosition === "above"),
    });
  }

  _checkAvailability({
    startDate,
    endDate,
    numNights,
  }: BookingInfo) {
    const { fetchAvailability, poi } = this.props;

    this.props.selectDates({ startDate, endDate, numNights });

    if (startDate && endDate) {
      fetchAvailability({
        id: poi.id,
        startDate,
        endDate,
        numNights,
      });
    }
  }

  _book(data: BookingInfo,
    poi: Object,
    provider: Provider,
  ) {
    const { partnerClick } = this.props;

    partnerClick({
      type: poi.subtypes[0],
      provider: provider.data.id,
      name: poi.name,
    });

    if (provider.type === "bdc") {
      window.open(bookingDotcomUrl({
        checkin: data.startDate,
        checkout: data.endDate,
        hotelId: provider.data.id,
        label: "dotcom-pois",
      }, provider.data.website).replace("https", "http"));
    }
    if (provider.type === "hostelworld") {
      window.open(hostelWorldUrl({
        dateFrom: data.startDate,
        propNum: provider.data.id,
        dateTo: data.endDate,
      }));
    }
  }

  _bookingProps(
    poi: Object,
    availability: Availability,
    provider: Provider,
    place: Object,
    categoryLink: string,
  ) {
    const hasDatesButNoPrice = (availability.startDate && availability.endDate) &&
      (!availability.fetching && !availability.price);

    const message = {
      string: "Check availability",
      type: "heading",
      link: {},
    };

    if (hasDatesButNoPrice) {
      message.string = "Unavailable on these dates";
      message.type = "error";
      message.link = {
        place: place && place.name,
        type: poiTypes[poi.poiType].displayName.toLowerCase(),
        url: categoryLink,
      };
    }

    return {
      type: "hotel",
      onSubmit: (data: BookingInfo) => this._book(data, poi, provider),
      message,
      price: !availability.fetching && availability.price ? {
        amount: availability.price,
        currency: availability.currency,
        rate: availability.numNights > 0 ?
          `${availability.numNights} night${availability.numNights !== 1 ? "s" : ""}` : "",
      } : {
        amount: 0,
        currency: "USD",
        rate: "",
      },
      startDate: availability.startDate,
      endDate: availability.endDate,
      buttonLabel: "Select your room",
      onSelectDates: this._checkAvailability,
      withFullScreenPortal: this.props.mobile,
      getPriceOnLoad: (availability.startDate !== null && availability.endDate !== null),
      soldOut: hasDatesButNoPrice,
      searching: availability.fetching,
    };
  }

  render() {
    const {
      poi,
      breadcrumb,
      place,
      availability,
      params,
    } = this.props;

    let { images } = this.props;

    const subtype = poiTypes[poi.poiType.toLowerCase()].displayName;

    const provider = poi.providers && hotelProvider(poi.providers);

    if (poi.review) {
      Object.keys(poi.review).forEach((review) => {
        poi.review[review] = cleanUp.removeBackslash(poi.review[review]);
      });
    }

    const tinyHeading = {
      level: 4,
      size: "tiny",
      weight: "thick",
      importance: "high",
      caps: true,
    };

    const name = poi.name;
    const poiId = `poi-${poi.id}`;
    const categoryLink = createListUrl({
      resource: poi.type,
      breadcrumb: breadcrumb.map(b => b.title),
      placeId: place.id,
    });

    const currentBreadcrumb = {
      title: subtype,
      href: categoryLink,
    };
    const amenitiesItems = provider && HotelPoiComponent._getAmenities(provider.data.facilities);

    let descriptionLink = "";

    // TODO: add a test to cover this
    if (provider) {
      if (provider.type === "bdc") {
        images = images.reverse();
        descriptionLink = bookingDotcomUrl({
          checkin: availability.startDate,
          checkout: availability.endDate,
          hotelId: provider.data.id,
          label: "dotcom-pois",
        }, provider.data.website);
      }

      if (provider.type === "hostelworld") {
        descriptionLink = hostelWorldUrl({
          dateFrom: availability.startDate,
          propNum: provider.data.id,
          dateTo: availability.endDate,
        });
      }
    }

    return (
      <div className="PageContainer" id={poiId} key={poiId}>
        <DataLayer
          data={{
            cd26_PartnerOfferingDetails: name,
            cd27_PartnerOfferingKind: subtype,
            cd28_PartnerName: provider && provider.name,
          }}
        />
        <Breadcrumbs
          links={[...breadcrumb, currentBreadcrumb]}
        />

        <div className="Page Page--detail">
          <PageHeader
            heading={name}
            title={`${place.name} ${subtype}`}
            titleHref={categoryLink}
            topChoice={poi.poi && poi.poi.poiAttributes && poi.poi.poiAttributes.length &&
              poi.poi.poiAttributes[0].name === "top_choice"}
            type={poi.subtypes[0]}
            stars={poi.starRating}
            place={poi.containingPlace.name}
            alignment="center"
            contained
          />

          <div className="PageContent">
            {this.props.marked !== null &&
              <div>
                <BookmarkButtonAlt
                  className="js-gtm-bookmark-button"
                  onClick={this.props.onClickBookmark}
                  marked={this.props.marked}
                  style={styles.bookmarkButtonAlt}
                />
                <FeatureDiscover
                  featureName="firstPoiSeen"
                  removeOnOuterClick
                  removeOnNavigate
                >
                  <DiscoveryFlyout
                    headline="Save this place"
                    description="Tap the bookmark to save and organize your interests into lists."
                    style={styles.featureDiscovery.button}
                  />
                </FeatureDiscover>
              </div>
            }

            {images && images.length > 0 && this.props.mobile &&
              <ImagesContainer images={images} mobile />
            }

            <aside className="PageAside" data-hide-map={this.state.hideMap}>
              <Sticky
                enabled={!this.props.mobile}
                top={30}
                bottomBoundary={`#${poiId} .PageContent`}
              >
                {provider &&
                  <Booking
                    {...this._bookingProps(poi, availability, provider, place, categoryLink)}
                  />
                }

                {provider && false &&
                  <SidebarSection title="Check availability" first>
                    <Booking
                      {...this._bookingProps(poi, availability, provider, place, categoryLink)}
                    />
                  </SidebarSection>
                }

                <EssentialInfo
                  poi={poi}
                  place={place}
                  mobile={this.props.mobile}
                  links={breadcrumb}
                  showPrice={false}
                />

                {(poi.telephone || poi.url) &&
                  <ContactBar
                    phone={poi.telephone}
                    website={poi.website}
                  />
                }
              </Sticky>
            </aside>

            <article className="PageArticle">
              {images && images.length > 0 && !this.props.mobile &&
                <ImagesContainer images={images} />
              }

              <div className="PageArticle-content">
                <article>
                  {poi.review && poi.review.essential &&
                    <ContentBlock
                      htmlContent={poi.review.essential + poi.review.extension}
                    />
                  }

                  {provider && provider.data.description.length > 0 &&
                    <ContentBlock
                      heading="Property description"
                      headerLink={{
                        label: provider.name && `via ${provider.name}`,
                        href: descriptionLink.replace("https", "http"),
                      }}
                      htmlContent={
                        cleanUp.replaceNewlineWithParagraph(provider.data.description[0].text)
                      }
                    />
                  }

                  {false &&
                    <Callout
                      type="book"
                      align="center"
                      heading="Lonely Planet’s Best in Travel 2016"
                      slug="/"
                      price={{
                        currency: "USD",
                        amount: 21.99,
                      }}
                      description={`Be an in-the-know traveler this year with Lonely
                        Planet’s collection of the hottest trends, destinations,
                        journeys.`}
                      image="http://dummyimage.com/132x168/4d494d/686a82.gif"
                    />
                  }

                  {provider && provider.data.description[1] &&
                    <ContentBlock
                      heading={provider.data.description[1].title}
                      htmlContent={
                        cleanUp.replaceNewlineWithParagraph(provider.data.description[1].text)
                      }
                    />
                  }

                  <footer className="ContentFooter">
                    {poi.review && poi.review.essential &&
                      <div className="ContentFooter-text">
                        <EditLink url={editUrl(poi, place, breadcrumb)} />
                      </div>
                    }

                    <div className="ContentFooter-share">
                      <ShareMenu
                        text={poi.name}
                        url={`https://www.lonelyplanet.com${this.getPoiUrl()}`}
                        mobile={this.props.mobile}
                      />
                    </div>
                  </footer>
                </article>

                <aside>

                  {amenitiesItems && amenitiesItems.length > 0 &&
                    <section className="ContentSection">
                      <ContentHeader
                        title={`Features of ${poi.name}`}
                        heading={tinyHeading}
                        border="bottom"
                      />

                      <Amenities
                        columns={3}
                        listType="grouped"
                        items={amenitiesItems}
                      />
                    </section>
                  }

                  {poi.poi && poi.poi.location &&
                    <section className="ContentSection ContentSection-nearbyPlaces">
                      <ContentHeader
                        title="Nearby & Noteworthy"
                        heading={tinyHeading}
                        border="bottom"
                      />

                      <NearbyPlaces
                        currentPlace={poi.poi}
                        mapId={`leaflet-map-container-${poi.id}`}
                        relatedPlaceId={params.placeId}
                      />

                      <Waypoint
                        onEnter={this.enterNearbyPlaces}
                        onLeave={this.leaveNearbyPlaces}
                        fireOnRapidScroll
                      />
                    </section>
                  }
                </aside>
              </div>
            </article>
          </div>
        </div>
      </div>
    );
  }
}

HotelPoiComponent.propTypes = {
  /**
   * The POI for this component
   */
  poi: React.PropTypes.object.isRequired,

  /**
   * The place data for the POI
   */
  place: React.PropTypes.object.isRequired,

  /**
   * An array of images
   */
  images: React.PropTypes.array.isRequired,

  /**
   * Whether or not the layout should be formatted for mobile screen sizes
   */
  mobile: React.PropTypes.bool,

  /**
   * List of availability for
   */
  availability: React.PropTypes.object,

  /**
   * Action creator for fetchAvailability
   */
  fetchAvailability: React.PropTypes.func,

  /**
   * Action creator for partnerClick
   */
  partnerClick: React.PropTypes.func,

  /**
   * React router params
   */
  params: React.PropTypes.object,

  /**
   * Dispatch method for selecting dates
   *
   */
  selectDates: React.PropTypes.func,

  /**
   * The breadcrumbs for this page
   */
  breadcrumb: React.PropTypes.arrayOf(Object),
  onClickBookmark: React.PropTypes.func,
  marked: React.PropTypes.bool,
};

const avail = _.memoize(getAvailability);
const mapStateToProps = (state) => ({
  mobile: state.mobile.isMobile,
  availability: avail(state.availability),
  resource: state.resource,
  kind: state.kind,
  breadcrumb: state.breadcrumb,
});

const mapDispatchToProps = (dispatch) => Object.assign({},
  bindActionCreators(actions, dispatch),
  bindActionCreators(analytics, dispatch),
  bindActionCreators({ selectDates }, dispatch),
);

const connected = connect(mapStateToProps, mapDispatchToProps)(HotelPoiComponent);
export { connected };



// WEBPACK FOOTER //
// ./assets/pois/components/hotel.jsx